import React from 'react';
import styled from 'styled-components';
import { t } from 'ttag';
import { Category, Item } from '../../../../contexts/site/site-context';
import MenuItem from './items/MenuItem';
import { Preferences } from "../MenuPage";
import useOperatorContext from "../../../../contexts/operator/useOperatorContext";
import formatAvailability from "../../../../utils/formatAvailability"

type MenuCategoryProps = {
    category: Category;
    showItemModal: (item: Item, edit: boolean) => void;
    menuItemStyle?: string;
    isFirst?: boolean;
    preferences: Preferences;
    bannerOffset?: boolean;
    bannerOffsetHeight?: number;
};

const MenuCategory = ({ category, showItemModal, menuItemStyle = 'landscape' , isFirst = false, preferences, bannerOffset, bannerOffsetHeight }: MenuCategoryProps) => {

    const operatorContext = useOperatorContext();

    const renderMenuItems = () => {
        return category.items.map((item, index) =>
            <MenuItem
                key={`mcmi-${item.uid}`}
                item={item}
                showItemModal={showItemModal}
                preferences={preferences}
                variant={menuItemStyle}
            />
        );
    };

    if (category.is_hidden) {
        return null;
    }

    const visibleItems = category.items.filter((i) => {

        if ( i.is_vegan && !preferences.vegan ) { return false }
        if ( i.is_vegetarian && !preferences.vegetarian ) { return false }
        if ( i.is_gluten_free && !preferences.glutenFree ) { return false }
        if ( i.is_halal && !preferences.halal ) { return false }
        if ( i.is_kosher && !preferences.kosher ) { return false }

        return true;

    }).length;

    if ( visibleItems === 0 ) {
        return null;
    }

    return (
        <>
            <StyledCategory id={(category.uid as unknown) as string} className="menu-category" bannerOffset={bannerOffset} bannerOffsetHeight={bannerOffsetHeight}>
                <CategoryHeader className="menu-category__header">
                    <div className='d-flex'>
                        <h1>{category.name}</h1>
                    </div>
                    { category.description && (
                        <h4 className={`description ${isFirst ? 'first' : ''}`}>
                            {category.description}
                        </h4>
                    )}
                    { category.schedule && (
                        <h4 className={`description ${isFirst ? 'first' : ''}`}>
                            <span className="material-symbols-outlined">schedule</span> {t`Available`} { formatAvailability(category.schedule) }
                        </h4>
                    )}
                </CategoryHeader>
                <CategoryItems className="menu-category__content row">
                    {renderMenuItems()}
                </CategoryItems>
            </StyledCategory>
        </>
    );
};

const StyledCategory = styled.div<{ bannerOffset: boolean | undefined, bannerOffsetHeight: number | undefined }>`
    //${(props) => props.bannerOffset ? `scroll-margin-top: calc(72px + ${props.bannerOffsetHeight}px);` : 'scroll-margin-top: 72px;'}
    scroll-margin-top: 72px;
    border-radius: var(--radius_xl);
    overflow: visible;
    position: relative;

    @media screen and (min-width: 768px) {
        overflow: initial;
    }

    &:nth-child(even) {
        .menu-category__timings { display: none; }
    }

     &:nth-child(odd) .menu-category__timings {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0;
        margin-top: 4px;
        margin-bottom: 0;
        color: var(--brand);
        @media screen and (min-width: 768px) {
            font-weight: 500;
        }
    }

    hr {
        position: relative;
        left: -16px;
        width: 100vw;
    }
`;

const CategoryHeader = styled.div`
    padding: 0 16px;
    margin-bottom: 12px;
    font-size: 24px;
    line-height: 32px;

    h1 {
        margin-bottom: 0;
    }

    h4 {
        font-family: var(--text_body_font);
        letter-spacing: var(--text_body_font_spacing);
        text-transform: var(--text_body_font_case);
    }

    .description {
        color: var(--text-black);
        margin-top: 4px;
        margin-bottom: 0;

        @media screen and (min-width: 576px) {
            font-size: var(--core_text_ui_lg_desktop);
        }

        .material-symbols-outlined {
            margin-right: 4px;
            vertical-align: bottom;
        }
    }

    @media screen and (min-width: 768px) {
        padding: 0 48px;

        h1 {
            padding-top: 0 !important;
        }
    }
`;
const CategoryItems = styled.div`
    display: grid;
    margin: 0;
    margin-bottom: 24px;

    ${(props) => props.theme.v4.card_style === 'landscape' && `
        grid-template-columns: repeat(1, minmax(0, 1fr));

        @media screen and (min-width: 993px) {
            grid-template-columns: repeat(2, minmax(0, 1fr))
        }

        @media screen and (min-width: 768px) {
            padding: 0 48px;
            gap: 24px;
        }
    `}

    ${(props) => props.theme.v4.card_style === 'portrait' && `
        grid-template-columns: repeat(2, minmax(160px, 1fr));
        gap: 16px;
        padding: 0 16px;

        @media screen and (min-width: 768px) {
            grid-template-columns: repeat(auto-fill, minmax(282px, 1fr));
            gap: 24px;
            padding: 0 48px;
        }
    `}
`;

export default MenuCategory;
